import React from "react";
import {makeRequest} from "../../services/requestService";
import {API_URL} from "../../constants";
import PropTypes from "prop-types";

export const NODE_TYPE = {
    ROOT: "content-root",
    CONTENT: "content",
    TEXT: "text",
    LINE: "line"
}

export const TEXT_NODE_VIEW = {
    TREE: "tree",
    TEXT: "text",
    LABEL: "label"
}

export const defaultTreeState = {
    id: "content-root",
    label: "loading",
    type: NODE_TYPE.ROOT,
    boundingBox: "",
    factor: "",
    children: []
}

useTree.propTypes = {
    issue: PropTypes.shape({
        id: PropTypes.number.isRequired
    }),
    page: PropTypes.shape({
        id: PropTypes.number.isRequired,
        viewing: PropTypes.string.isRequired,
        regions: PropTypes.array.isRequired,
        x: PropTypes.number.isRequired,
        y: PropTypes.number.isRequired,
        w: PropTypes.number.isRequired,
        h: PropTypes.number.isRequired,
    }),
    textMode: PropTypes.string
}

export function useTree(issue, page, textMode = "structured") {
    const [contents, setContents] = React.useState(null);
    const [texts, setTexts] = React.useState(null);
    const [treeData, setTreeData] = React.useState(defaultTreeState);
    const [loadingText, setLoadingText] = React.useState(true);
    const [loadingContent, setLoadingContent] = React.useState(true);
    const [updated, setUpdated] = React.useState(false);

    const fetchContent = async () => {
        let issueId = issue.id;
        const contentRequest = await makeRequest(`${API_URL}/contents/byissue/${issueId}/?size=-1`);
        if (contentRequest.status === 200) {
            setContents(contentRequest.data.data);
            return true;
        }
        return false;
    }

    const fetchTexts = async () => {
        let pageId = page.id;
        const textRequest = await makeRequest(`${API_URL}/texts/bypage/${pageId}?page=0&size=0`);
        if (textRequest.status === 200) {
            setTexts(textRequest.data.data);
            return true;
        }
        return false;
    }

    // fetch Contents
    React.useEffect(() => {
        setLoadingContent(true);
        fetchContent().then((isLoaded) => {
            if (isLoaded)
                setLoadingContent(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [issue]);

    // fetch Texts
    React.useEffect(() => {
        setLoadingText(true);
        fetchTexts().then((isLoaded) => {
            if (isLoaded)
                setLoadingText(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // create tree
    React.useEffect(() => {
        if (!loadingContent && !loadingText)
            reset();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingContent, loadingText, textMode]);

    // update structure
    React.useEffect(() => {
        if (updated)
            refresh();
        setUpdated(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updated]);

    const reset = () => {
        if (!loadingText && !loadingContent) {
            let tree = createTree();
            setTreeData(tree);
        }
    }

    const reload = async () => {
        await fetchContent();
        await fetchTexts();
        setUpdated(true);
    }

    const refresh = () => {
        if (!loadingText && !loadingContent) {
            setTreeData(prevState => {
                if (textMode === "raw")
                    refreshRegion(prevState, page.regions);
                else
                    refreshContent(prevState, contents);
                return {...prevState, label: page.viewing.split(".")[0]};
            });
        }
    }

    const refreshContent = (node, contents) => {
        contents.forEach((content) => {
            if (!contentHasChild(content))
                return;
            const contentChildNode = node.children.find((nodeChild) => nodeChild.id === `${NODE_TYPE.CONTENT}-${content.id}`);
            if (contentChildNode) {
                contentChildNode.data = content;
                refreshRegion(contentChildNode, content.regions);
                refreshContent(contentChildNode, content.children);
            } else {
                let child = createNodeFromContent(content);
                if (child) {
                    child.parent = node;
                    node.children.push(child);
                }
            }
        });
    }

    const refreshRegion = (node, regions) => {
        regions.forEach((region) => {
            let regionChildNode = node.children.find((nodeChild) => nodeChild.id === `${NODE_TYPE.TEXT}-${region}`);
            if (regionChildNode) { // update if child exist
                refreshLine(regionChildNode, region);
            } else { // or create if not exist
                regionChildNode = createNodeFromRegion(region);
                if (regionChildNode) {
                    regionChildNode.parent = node;
                    node.children.push(regionChildNode);
                }
            }
        });
    }

    const refreshLine = (node, region) => {
        let text = texts.find(text => text.id === region);
        if (text) {
            let children = [...node.children];
            // add new line
            text.lines.forEach((line) => {
                let lineNode = children.find((ln) => ln.id === `${NODE_TYPE.LINE}-${line.id}`);
                if (lineNode) {
                    const index = children.indexOf(lineNode);
                    if (index > -1)
                        children.splice(index, 1);
                    lineNode.data = line;
                    lineNode.boundingBox = `${line.x},${line.y},${line.w},${line.h}`;
                } else {
                    lineNode = createNodeFromLine(line);
                    lineNode.parent = node;
                    node.children.push(lineNode);
                }
            });
            // delete deleted line
            children.forEach((child) => {
                const index = node.children.indexOf(child);
                if (index > -1)
                    node.children.splice(index, 1);
            })
            node.children.sort((a, b) => {
                return a.data.readingOrder - b.data.readingOrder;
            });
            node.label = text.cachedContent.substr(0, 128) + "...";
            node.data = text;
        }
    }

    const findNodeByID = (currentNode, nodeID) => {
        if (currentNode.id === nodeID) {
            return currentNode;
        }
        if (Array.isArray(currentNode.children))
            for (let child of currentNode.children) {
                let result = findNodeByID(child, nodeID);
                if (result)
                    return result;
            }
        return null;
    }

    const getPathTabOfNode = (currentNode, nodeID, parentTab) => {
        if (currentNode.id === nodeID) {
            return [...parentTab, nodeID];
        }
        if (Array.isArray(currentNode.children)) {
            for (let child of currentNode.children) {
                let result = getPathTabOfNode(child, nodeID, [...parentTab, currentNode.id]);
                if (result[result.length - 1] === nodeID)
                    return result;
            }
        }
        return parentTab;
    }

    const contentHasChild = (content) => {
        let r = false;
        for (let regionID of content.regions) {
            r = r || Boolean(texts.find(text => text.id === regionID));
            if (r) return r;
        }
        content.children.forEach((child) => {
            r = r || contentHasChild(child);
        });
        return r;
    }

// tree creator

    const createNodeFromLine = (line) => {
        let node = {
            id: `${NODE_TYPE.LINE}-${line.id}`,
            label: line.cachedContent,
            type: NODE_TYPE.LINE,
            boundingBox: `${line.x},${line.y},${line.w},${line.h}`,
            factor: page.w,
            data: line,
            parent: undefined
        }
        return node;
    }

    const createNodeFromRegion = (region) => {
        let text = texts.find(text => text.id === region);
        if (!text)
            return null;
        let label = "" + text.cachedContent;
        label = label.substr(0, 128) + "...";
        let node = {
            id: `${NODE_TYPE.TEXT}-${text.id}`,
            label: label,
            type: NODE_TYPE.TEXT,
            data: text,
            editable: false,
            viewMode: TEXT_NODE_VIEW.TREE,
            boundingBox: `${text.region.x},${text.region.y},${text.region.w},${text.region.h}`,
            factor: page.w,
            parent: undefined,
            children: []
        }
        text.lines.forEach((line) => {
            let child = createNodeFromLine(line);
            child.parent = node;
            node.children.push(child);
        });
        return node;
    }

    const createNodeFromContent = (content) => {
        if (!contentHasChild(content))
            return null;
        let nodeID = `${NODE_TYPE.CONTENT}-${content.id}`;
        let node = {
            id: nodeID,
            label: content.label ? content.label : nodeID.toUpperCase(),
            type: NODE_TYPE.CONTENT,
            boundingBox: "",
            factor: page.w,
            data: content,
            parent: undefined,
            children: []
        }
        content.regions.forEach((region) => {
            let text = texts.find(text => text.id === region);
            if (text) {
                let type = text.type.toUpperCase();
                if (type === "HEADING")
                    node.label = text.cachedContent;
            }
            let child = createNodeFromRegion(region);
            if (child) {
                child.parent = node;
                node.boundingBox += ` ${child.boundingBox}`;
                node.children.push(child);
            }
        });
        content.children.forEach((childContent) => {
            let child = createNodeFromContent(childContent);
            if (child) {
                child.parent = node;
                node.boundingBox += ` ${child.boundingBox}`;
                node.children.push(child);
            }
        });
        node.boundingBox = node.boundingBox.trim();
        return node;
    }

    const createTree = () => {
        let tree = {
            id: `${NODE_TYPE.CONTENT}-root`,
            label: "loading",
            type: NODE_TYPE.ROOT,
            boundingBox: `${page.x},${page.y},${page.w},${page.h}`,
            factor: page.w,
            children: []
        };
        if (textMode === "raw") {
            page.regions.forEach((region) => {
                let child = createNodeFromRegion(region);
                if (child) {
                    child.parent = tree;
                    tree.children.push(child);
                }
            });
        } else {
            contents.forEach((content) => {
                let child = createNodeFromContent(content);
                if (child) {
                    child.parent = tree;
                    tree.children.push(child);
                }
            });
        }
        tree.label = page.viewing.split(".")[0];
        return tree;
    }

// Text event

    const toggleEditState = (node) => {
        if (node.type === NODE_TYPE.TEXT) {
            node.editable = !node.editable;
            setTreeData(prevState => ({...prevState}));
        }
    }

    const handleChangeViewMode = (node, newMode) => {
        if (node.type === NODE_TYPE.TEXT) {
            switch (newMode) {
                case TEXT_NODE_VIEW.TEXT:
                    node.viewMode = TEXT_NODE_VIEW.TEXT;
                    break;
                case TEXT_NODE_VIEW.LABEL:
                    node.viewMode = TEXT_NODE_VIEW.LABEL;
                    break;
                default:
                    node.viewMode = TEXT_NODE_VIEW.TREE;
            }
            setTreeData(prevState => ({...prevState}));
        }
    }

// Line event

    const onValueChange = (node, newValue) => {
        if (node.type === NODE_TYPE.LINE) {
            node.label = newValue;
            setTreeData(prevState => ({...prevState}));
        }
    }

    return {
        // tree object
        treeData,
        // reset tree
        reset,
        // fetch and reset the tree
        reload,
        // fetch and update the tree
        refresh,
        // find a node by id from the node in parameter
        findNodeByID,
        // return the table of ids of all nodes up to the node passed in parameter
        getPathTabOfNode,
        // toggle editable state of text node
        toggleEditState,
        // switch modes
        handleChangeViewMode,
        // edit Line node label
        onValueChange
    }
}